import React from 'react';

import {
  Box,
  Text,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  RadioGroup,
  Radio,
  Stack,
  Select,
  Textarea,
  HStack,
  Badge,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
} from '@chakra-ui/react';

import { useColors } from './colors';
import { getEntityId, getEntityName } from '../utils/entityOptions.util';

const SkuScopeSection = ({
  scopeMode,
  onScopeModeChange,
  selectedStoreIds,
  onStoreChange,
  selectedSupplierIds,
  onSupplierChange,
  manualSkus,
  onManualSkusChange,
  maxSkuLimit,
  onMaxSkuLimitChange,
  includeOptimizedRerun,
  onIncludeOptimizedRerunChange,
  includeHaltedRerun,
  onIncludeHaltedRerunChange,
  storeOptions = [],
  supplierOptions = [],
  embedded = false,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor, inputColor } = useColors();

  const manualSkuCount = manualSkus
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean).length;

  const showMaxLimit = scopeMode === 'all' || scopeMode === 'filter' || scopeMode === 'manual';

  const body = (
    <>
      {!embedded && (
        <>
          <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
            SKU scope
          </Text>
          <Text color={secondaryTextColor} fontSize="sm" mb={4}>
            Choose which catalog SKUs the optimizer run should target. One SKU can have multiple
            linked listings across stores.
          </Text>
        </>
      )}
      {embedded && (
        <Text color={secondaryTextColor} fontSize="sm" mb={4}>
          Choose which catalog SKUs the optimizer run should target. One SKU can have multiple linked
          listings across stores.
        </Text>
      )}

      <RadioGroup value={scopeMode} onChange={onScopeModeChange}>
        <Stack spacing={3}>
          <Radio value="all" colorScheme="teal">
            <Text fontSize="sm" color={textColor}>
              All SKUs in catalog
            </Text>
          </Radio>
          <Radio value="filter" colorScheme="teal">
            <Text fontSize="sm" color={textColor}>
              Filter by store and/or supplier
            </Text>
          </Radio>
          <Radio value="manual" colorScheme="teal">
            <Text fontSize="sm" color={textColor}>
              Manual SKU list
            </Text>
          </Radio>
        </Stack>
      </RadioGroup>

      {scopeMode === 'filter' && (
        <VStack align="stretch" spacing={4} mt={4}>
          <FormControl>
            <FormLabel color={textColor} fontSize="sm">
              Stores
            </FormLabel>
            <Select
              placeholder="All stores"
              value={selectedStoreIds[0] || ''}
              onChange={(e) => onStoreChange(e.target.value ? [e.target.value] : [])}
              color={inputColor}
              size="sm"
            >
              {storeOptions.map((store) => (
                <option key={getEntityId(store)} value={getEntityId(store)}>
                  {getEntityName(store)}
                </option>
              ))}
            </Select>
            <FormHelperText>
              SKUs with a linked listing on this store. Combined with a supplier, both must match.
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel color={textColor} fontSize="sm">
              Suppliers
            </FormLabel>
            <Select
              placeholder="All suppliers"
              value={selectedSupplierIds[0] || ''}
              onChange={(e) => onSupplierChange(e.target.value ? [e.target.value] : [])}
              color={inputColor}
              size="sm"
            >
              {supplierOptions.map((supplier) => (
                <option key={getEntityId(supplier)} value={getEntityId(supplier)}>
                  {getEntityName(supplier)}
                </option>
              ))}
            </Select>
            <FormHelperText>
              SKUs tied to this supplier. With a store selected, the run uses SKUs matching both.
            </FormHelperText>
          </FormControl>
        </VStack>
      )}

      {(scopeMode === 'manual' || scopeMode === 'all') && (
        <FormControl mt={4}>
          <FormLabel color={textColor} fontSize="sm">
            eBay API store
          </FormLabel>
          <Select
            placeholder="Select store for Browse / GetItem APIs"
            value={selectedStoreIds[0] || ''}
            onChange={(e) => onStoreChange(e.target.value ? [e.target.value] : [])}
            color={inputColor}
            size="sm"
          >
            {storeOptions.map((store) => (
              <option key={getEntityId(store)} value={getEntityId(store)}>
                {getEntityName(store)}
              </option>
            ))}
          </Select>
          <FormHelperText>
            Required for verified seller search and GetItem calls during the run.
          </FormHelperText>
        </FormControl>
      )}

      {scopeMode === 'manual' && (
        <FormControl mt={4}>
          <HStack justify="space-between" mb={1}>
            <FormLabel color={textColor} fontSize="sm" mb={0}>
              SKU list
            </FormLabel>
            {manualSkuCount > 0 && (
              <Badge colorScheme="teal" variant="subtle">
                {manualSkuCount} SKU{manualSkuCount === 1 ? '' : 's'}
              </Badge>
            )}
          </HStack>
          <Textarea
            value={manualSkus}
            onChange={(e) => onManualSkusChange(e.target.value)}
            placeholder={'CAR-HO123\nCAR-FO456\nOne canonical SKU per line'}
            rows={6}
            fontFamily="mono"
            fontSize="sm"
            color={inputColor}
          />
          <FormHelperText>
            Enter canonical SKUs exactly as stored in SKU management (one per line).
          </FormHelperText>
        </FormControl>
      )}

      {showMaxLimit && (
        <FormControl mt={4}>
          <FormLabel color={textColor} fontSize="sm">
            Max SKUs per run
          </FormLabel>
          <NumberInput
            min={1}
            max={50000}
            value={maxSkuLimit}
            onChange={(_, val) => onMaxSkuLimitChange(Number(val) || 1)}
            size="sm"
          >
            <NumberInputField color={inputColor} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>
            {scopeMode === 'manual'
              ? 'Processes up to this many SKUs from your list (in list order), after exclusions.'
              : 'Processes the first N SKUs matching your filter (sorted by canonical SKU).'}
          </FormHelperText>
        </FormControl>
      )}

      <FormControl mt={4}>
        <Checkbox
          isChecked={includeOptimizedRerun}
          onChange={(e) => onIncludeOptimizedRerunChange(e.target.checked)}
          colorScheme="teal"
        >
          <Text fontSize="sm" color={textColor} ml={2}>
            Include already optimized SKUs (rerun)
          </Text>
        </Checkbox>
        <FormHelperText ml={6}>
          By default, SKUs with optimizer status &quot;optimized&quot; are skipped. Enable this to
          process them again.
        </FormHelperText>
      </FormControl>

      <FormControl mt={3}>
        <Checkbox
          isChecked={includeHaltedRerun}
          onChange={(e) => onIncludeHaltedRerunChange(e.target.checked)}
          colorScheme="teal"
        >
          <Text fontSize="sm" color={textColor} ml={2}>
            Include halted SKUs (rerun)
          </Text>
        </Checkbox>
        <FormHelperText ml={6}>
          By default, SKUs halted after 3 unsuccessful optimizer runs are skipped. Enable this to
          try them again.
        </FormHelperText>
      </FormControl>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
    >
      {body}
    </Box>
  );
};

export default SkuScopeSection;
